// Competitive leaderboards and participation are intentionally separate.
// This normalizer is a narrow client-side safety net: the API remains the
// authority, but a stale or malformed cached payload must never render a
// zero-point player or a duplicate user row.
export const TRIVIA_LEADERBOARD_CACHE_KEY = 'lbCacheV4';

export function createTriviaLeaderboardCacheKey({ scope, sign, country, userId, period, day = '' }) {
  const normalizedPeriod = String(period || 'month');
  return JSON.stringify({
    scope: String(scope || 'global'),
    sign: String(sign || ''),
    cc: String(country || ''),
    uid: String(userId || ''),
    period: normalizedPeriod,
    // A daily leaderboard is authoritative only for one Buenos Aires calendar
    // day. Month entries deliberately remain month-scoped.
    day: normalizedPeriod === 'today' ? String(day || '') : '',
  });
}

export function isCurrentTriviaLeaderboardCacheKey(key, todayDayKey) {
  try {
    const parsed = JSON.parse(String(key || ''));
    return parsed?.period !== 'today' || parsed.day === String(todayDayKey || '');
  } catch {
    return false;
  }
}

export function isActiveTriviaLeaderboardViewRequest(activeKey, requestKey) {
  return Boolean(activeKey) && activeKey === requestKey;
}

export function competitiveScoreForPeriod(row, period) {
  const rawScore = period === 'month'
    ? (row?.monthlyPoints ?? row?.score)
    : row?.score;
  const score = Number(rawScore);
  return Number.isFinite(score) ? score : 0;
}

export function normalizeCompetitiveLeaderboardRows(rows, period) {
  const byUserId = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const userId = String(row?.userId || '');
    const score = competitiveScoreForPeriod(row, period);
    if (!userId || userId.toLowerCase() === 'anon' || score <= 0 || byUserId.has(userId)) continue;
    byUserId.set(userId, row);
  }

  return Array.from(byUserId.values())
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

export function normalizeCompetitiveLeaderboardPayload(payload, period, currentUserId = '') {
  const source = payload && typeof payload === 'object' ? payload : {};
  const items = normalizeCompetitiveLeaderboardRows(source.items, period);
  const myScore = competitiveScoreForPeriod({
    score: source.myScore,
    monthlyPoints: source.myScore,
  }, period);
  const currentRow = currentUserId
    ? items.find((row) => String(row.userId) === String(currentUserId)) || null
    : null;
  const hasCompetitivePersonalScore = myScore > 0;

  return {
    ...source,
    items,
    // Preserve an API rank that is outside the visible top-N, but never allow
    // a zero-score participant to receive a competitive personal rank.
    myRank: hasCompetitivePersonalScore
      ? (currentRow?.rank ?? source.myRank ?? null)
      : null,
    myScore: source.myScore ?? null,
  };
}
